import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { jobSourcesApi } from '../../services/api';
import { SourceBoard } from '../../types';
import { errorMessage, fieldErrors } from '../../utils/errors';
import {
  INTERVAL_PRESETS,
  MAX_INTERVAL_HOURS,
  MIN_INTERVAL_HOURS,
  intervalHint,
  intervalLabel,
  parseWholeNumber,
} from '../../utils/jobSources';

interface Props {
  /** The board being edited; null keeps the dialog closed. */
  board: SourceBoard | null;
  globalHours: number;
  onClose: () => void;
  onSaved: (board: SourceBoard) => void;
}

const TITLE_ID = 'board-interval-title';

/** Give one board its own refresh interval, or put it back on the global one. */
const BoardIntervalDialog: React.FC<Props> = ({ board, globalHours, onClose, onSaved }) => {
  const [own, setOwn] = useState(false);
  const [hours, setHours] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Keep the last board on screen while the dialog closes.
  const [shown, setShown] = useState<SourceBoard | null>(board);

  useEffect(() => {
    if (!board) return;
    setShown(board);
    setOwn(board.refresh_interval_hours !== null);
    setHours(String(board.refresh_interval_hours ?? globalHours));
    setError('');
    setSaving(false);
  }, [board, globalHours]);

  const number = parseWholeNumber(hours);
  const preset = own && number !== null && INTERVAL_PRESETS.includes(number) ? number : null;

  const save = async () => {
    if (!board || saving) return;
    let value: number | null = null;
    if (own) {
      if (number === null) {
        setError('Enter a whole number of hours.');
        return;
      }
      value = number;
    }
    setSaving(true);
    setError('');
    try {
      const { board: next } = await jobSourcesApi.patchBoard(board.id, { refresh_interval_hours: value });
      onSaved(next);
    } catch (e) {
      setError(fieldErrors(e).refresh_interval_hours || errorMessage(e, 'Could not save the interval.'));
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(board)} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby={TITLE_ID}>
      <DialogTitle id={TITLE_ID}>Refresh interval for {shown?.company_name || shown?.board_token}</DialogTitle>
      <DialogContent>
        <form
          id="board-interval-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <FormControl>
            <RadioGroup
              aria-label="Which interval this board uses"
              value={own ? 'own' : 'global'}
              onChange={(e) => {
                setOwn(e.target.value === 'own');
                setError('');
              }}
            >
              <FormControlLabel
                value="global"
                control={<Radio />}
                label={`Use the global interval (${intervalLabel(globalHours)})`}
              />
              <FormControlLabel value="own" control={<Radio />} label="Give this board its own interval" />
            </RadioGroup>
          </FormControl>
          {own && (
            <Stack direction="row" alignItems="flex-start" sx={{ flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={preset}
                onChange={(_, value: number | null) => {
                  if (value !== null) {
                    setHours(String(value));
                    setError('');
                  }
                }}
                aria-label="Interval presets"
              >
                {INTERVAL_PRESETS.map((option) => (
                  <ToggleButton key={option} value={option} aria-label={`Every ${intervalLabel(option)}`} sx={{ px: 1.5, textTransform: 'none' }}>
                    {option}h
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <TextField
                label="Hours"
                type="number"
                size="small"
                value={hours}
                onChange={(e) => {
                  setHours(e.target.value);
                  setError('');
                }}
                error={Boolean(error)}
                helperText={error || intervalHint(number)}
                inputProps={{ min: MIN_INTERVAL_HOURS, max: MAX_INTERVAL_HOURS, step: 1, inputMode: 'numeric' }}
                sx={{ width: 190 }}
              />
            </Stack>
          )}
          {!own && error && (
            <Typography variant="body2" color="error" role="alert" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" form="board-interval-form" variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" aria-label="Saving" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardIntervalDialog;
